import React, { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardContentProps,
  Collapse,
  IconButton,
  IconProps,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

import { useFlag } from '@unleash/proxy-client-react';
import { TP_COLORS } from '../../../../constants';
import {
  getSubTopicState,
  IGroupedSubTopic,
  IGroupedTopic,
} from '../../../../store/SubTopicSlice';
import { useAppSelector } from '../../../../hooks';
import { getSubjectColor, getUrlConvertedString } from '../../../../helper';
import BasicLoader from '../../../../components/Loaders/BasicLoader';
import { whiteButtonStyles } from '../../../../components/Button/styles';
import {
  getProgressState,
  ISubjectForProgress,
} from '../../../../store/ProgressSlice';
import { getAuthState } from '../../../../store/AuthSlice';
import CircularProgressWithValue from './CircularProgressWithValue';

const iconProps: IconProps['sx'] = {
  color: 'primary',
  fontSize: 'small',
  style: {
    marginRight: '8px',
  },
};

interface IListItem {
  id: string;
  key?: string;
  title: string;
  cardContentProps?: CardContentProps;
  hideCaret?: boolean;
  isSelected: boolean;
  onClick?: () => void;
  href?: string;
  isSubTopicsOpen?: boolean;
  showProgress?: boolean;
  progress?: number;
  isCompleted?: boolean;
}

interface INavBarContentsProps {
  isNavBarExpanded?: boolean;
}

export const navBarWidth = 300;

export const navBarCollapseWith = 30;

const RENDER_LIST_ITEM = ({
  key,
  title,
  cardContentProps,
  hideCaret,
  isSelected,
  onClick,
  href,
  id,
  isSubTopicsOpen,
  showProgress,
  progress,
  isCompleted,
}: IListItem) => {
  const RENDER_CARET = () => {
    if (showProgress && hideCaret === true) {
      return isCompleted ? (
        <CheckCircleIcon {...iconProps} />
      ) : (
        <RadioButtonUncheckedIcon {...iconProps} />
      );
    }

    if (hideCaret === true) {
      return null;
    }

    if (isSelected && !isSubTopicsOpen) {
      return (
        <ArrowDropDownIcon
          color={isSelected ? undefined : 'secondary'}
          style={{
            marginRight: '5px',
          }}
        />
      );
    }

    if (isSelected) {
      return (
        <ArrowDropUpIcon
          color={isSelected ? undefined : 'secondary'}
          style={{
            marginRight: '5px',
          }}
        />
      );
    }

    if (!isSelected) {
      return (
        <ArrowDropDownIcon
          color={isSelected ? undefined : 'secondary'}
          style={{
            marginRight: '5px',
          }}
        />
      );
    }

    return null;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Wrapper = ({ children, ...props }: any) =>
    href ? (
      <NavLink {...props} className="no-link-styles">
        {children}
      </NavLink>
    ) : (
      <>{children}</>
    );

  return (
    <Card
      id={id}
      key={key}
      elevation={0}
      sx={{
        backgroundColor: isSelected ? 'rgba(0,0,0,0.05)' : 'transparent',
      }}
    >
      <Wrapper to={href}>
        <CardActionArea onClick={onClick}>
          <CardContent style={{ padding: '16px 5px' }} {...cardContentProps}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack
                component="span"
                direction="row"
                alignItems="center"
                maxWidth="98%"
              >
                {RENDER_CARET()}
                <Typography
                  component={hideCaret ? 'h4' : 'h3'}
                  variant="subtitle2"
                  fontWeight={isSelected ? 600 : 500}
                  textAlign="left"
                  className={
                    hideCaret ? 'ellipsis-text' : 'ellipsis-text.two-lines'
                  }
                  color={isSelected ? undefined : 'secondary'}
                >
                  {title}
                </Typography>
              </Stack>
              {/* RIGHT SIDE ITEM CAN BE RENDERED HERE */}
              {showProgress && !hideCaret && (
                <CircularProgressWithValue
                  value={progress || 0}
                  progressProps={{ marginRight: '5px' }}
                />
              )}
            </Stack>
          </CardContent>
        </CardActionArea>
      </Wrapper>
    </Card>
  );
};

const NavBarContents: React.FC<INavBarContentsProps> = ({
  isNavBarExpanded = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  // Store
  const subTopicState = useAppSelector(getSubTopicState);
  const progressState = useAppSelector(getProgressState);
  const authState = useAppSelector(getAuthState);

  const { groupedTopicsWithSubTopicsData, subTopic } = subTopicState;
  const { progress } = progressState;
  const urlPatternVals = location.pathname.split('_').reverse();
  const urlSubTopicId = urlPatternVals[0].replace(/\//, '');
  const userRole = authState.user?.role.name;
  const isQuestionsListPage = !!(params.curriculum && params.subject);

  // State
  const [selectedTopic, setSelectedTopic] = useState<IGroupedTopic | undefined>(
    undefined
  );

  const [selectedSubTopic, setSelectedSubTopic] = useState<
    IGroupedSubTopic | undefined
  >(undefined);
  const [isSubTopicsOpen, setSubTopicsOpen] = useState<boolean>(true);
  const [subjectProgress, setSubjectProgress] = useState<
    ISubjectForProgress | undefined
  >(undefined);

  const defaultOrderEnabled = useFlag(
    'learning_platform_subtopic_default_order_enabled'
  );

  useEffect(() => {
    if (isNavBarExpanded) {
      scrollIntoSubTopicElement(
        selectedTopic?._id || '',
        selectedSubTopic?._id || ''
      );
    }
  }, [isNavBarExpanded]);

  useEffect(() => {
    if (
      groupedTopicsWithSubTopicsData &&
      groupedTopicsWithSubTopicsData.list &&
      groupedTopicsWithSubTopicsData.list.length
    ) {
      const foundTopic = groupedTopicsWithSubTopicsData.list.find((topicData) =>
        topicData.subTopics.some(
          (subTopicData) => subTopicData._id === urlSubTopicId
        )
      );

      if (foundTopic) {
        const foundSubTopic = foundTopic.subTopics.find(
          (subTopicItem) => subTopicItem._id === urlSubTopicId
        );

        if (foundSubTopic) {
          setSelectedTopic(foundTopic);
          setSelectedSubTopic(foundSubTopic);

          scrollIntoSubTopicElement(foundTopic._id, foundSubTopic._id);
        }
      }
    }
  }, [window.location.pathname, groupedTopicsWithSubTopicsData]);

  useEffect(() => {
    if (
      progress &&
      progress.subjects &&
      progress.subjects.length &&
      subTopic &&
      subTopic.topic
    ) {
      setSubjectProgress(
        progress.subjects.find(
          (item) =>
            item.subject === subTopic.topic.subject &&
            item.subjectCode === subTopic.topic.subjectCode &&
            item.subjectLevel === subTopic.topic.subjectLevel
        )
      );
    }
  }, [progress, subTopic]);

  const scrollIntoSubTopicElement = (
    elementTopicId: string,
    elementSubTopicId: string
  ) => {
    const _element = window.document.getElementById(
      `sub-topic-li-${elementTopicId}-${elementSubTopicId}`
    );

    if (_element) {
      setTimeout(() => {
        _element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });
      }, 500);
    }
  };

  const handleTopicChange = (topic: IGroupedTopic) => {
    if (topic._id === selectedTopic?._id) {
      setSubTopicsOpen((prevState) => !prevState);
    } else if (topic.subTopics.length) {
      setSubTopicsOpen(true);
      navigate(
        getSubTopicChangeUrl({
          _id: topic.subTopics[0]._id,
          name: topic.subTopics[0].name,
        })
      );
    }
  };

  const getSubTopicChangeUrl = (
    subTopicData: IGroupedSubTopic,
    isBackButton?: boolean
  ) => {
    const urlConvertedCurriculum = getUrlConvertedString(
      selectedTopic?.curriculum || ''
    );

    const urlConvertedSubject = getUrlConvertedString(
      selectedTopic?.subject || ''
    );

    const urlConvertedSubTopic = getUrlConvertedString(
      subTopicData?.name || ''
    );

    let url = `${process.env.PUBLIC_URL}/dashboard/my-quizzes/${
      isBackButton ? '' : 'resources/'
    }subtopic/${urlConvertedCurriculum}_${urlConvertedSubject}_${urlConvertedSubTopic}_${
      subTopicData._id
    }`;

    if (isQuestionsListPage) {
      url = `${process.env.PUBLIC_URL}/${urlConvertedCurriculum}/${urlConvertedSubject}/${urlConvertedSubTopic}_${subTopicData._id}`;
    }

    return url;
  };

  const getTopicProgress = (topicData: IGroupedTopic) => {
    let progressValue = 0;

    if (subjectProgress) {
      const topicProgress = subjectProgress.topics.find(
        (item) => item._id === topicData._id
      );

      if (topicProgress && topicProgress.subTopics.length) {
        const { subTopics } = topicProgress;

        const totalProgress = subTopics.reduce((accumulator, object) => {
          return accumulator + object.completion;
        }, 0);

        progressValue = totalProgress / (topicData.subTopics.length || 1);
      }
    }
    return progressValue;
  };

  const getSubTopicProgress = (topicId: string, subTopicId: string) => {
    let isCompleted = false;

    if (subjectProgress) {
      const topicProgress = subjectProgress.topics.find(
        (item) => item._id === topicId
      );

      if (topicProgress && topicProgress.subTopics.length) {
        const subTopicProgress = topicProgress.subTopics.find(
          (item) => item._id === subTopicId
        );

        if (subTopicProgress && subTopicProgress.completion === 100) {
          isCompleted = true;
        }
      }
    }
    return isCompleted;
  };

  const handleBackButtonClick = () => {
    const pathNames = location.pathname.split('/').reverse();

    if (pathNames.length && pathNames[1] === 'subtopic') {
      navigate(
        `${process.env.PUBLIC_URL}/${getUrlConvertedString(
          groupedTopicsWithSubTopicsData?.curriculum || ''
        )}`
      );
    } else if (isQuestionsListPage) {
      navigate(
        `${process.env.PUBLIC_URL}/${getUrlConvertedString(
          groupedTopicsWithSubTopicsData?.curriculum || ''
        )}/${getUrlConvertedString(
          groupedTopicsWithSubTopicsData?.subject || ''
        )}`
      );
    } else if (subTopic) {
      navigate(
        getSubTopicChangeUrl(
          {
            _id: subTopic._id,
            name: subTopic.name,
          },
          true
        )
      );
    } else {
      navigate(`${process.env.PUBLIC_URL}/`);
    }
  };

  if (
    (!groupedTopicsWithSubTopicsData ||
      (groupedTopicsWithSubTopicsData &&
        (!groupedTopicsWithSubTopicsData.list ||
          (groupedTopicsWithSubTopicsData.list &&
            groupedTopicsWithSubTopicsData.list.length === 0)))) &&
    subTopicState.status === 'loading'
  ) {
    return <BasicLoader />;
  }

  return (
    <Box>
      {groupedTopicsWithSubTopicsData &&
        groupedTopicsWithSubTopicsData.subject && (
          <div>
            <Card
              elevation={0}
              sx={{
                mb: '15px',
                background: getSubjectColor(
                  groupedTopicsWithSubTopicsData.subject
                ),
              }}
            >
              <CardContent style={{ paddingBottom: '16px', minHeight: '80px' }}>
                <Stack direction="row" alignItems="center">
                  <IconButton
                    color="inherit"
                    size="small"
                    onClick={handleBackButtonClick}
                    sx={{
                      ...whiteButtonStyles,
                      marginRight: '15px',
                      backgroundColor: 'rgba(255,255,255, 0.2)',
                      color: TP_COLORS.white,
                      p: '3px',
                    }}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography
                    component="h3"
                    variant="subtitle2"
                    fontWeight={600}
                    color={TP_COLORS.white}
                    className="ellipsis-text"
                  >
                    {groupedTopicsWithSubTopicsData.curriculum || ''}
                    <br />
                    {groupedTopicsWithSubTopicsData.subject || ''}&nbsp;(
                    {groupedTopicsWithSubTopicsData.subjectCode || ''})
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </div>
        )}

      <div>
        {groupedTopicsWithSubTopicsData &&
        groupedTopicsWithSubTopicsData.list &&
        groupedTopicsWithSubTopicsData.list.length
          ? groupedTopicsWithSubTopicsData.list.map((topicData, topicIndex) => {
              const subTopics = topicData.subTopics || [];
              const topicId = topicData._id;
              const isTopicSelected =
                selectedTopic?._id === topicId ||
                subTopics.some(
                  (subTopicData) => subTopicData._id === selectedSubTopic?._id
                );

              return (
                <div
                  key={`topic-item-${topicIndex}`}
                  style={{
                    backgroundColor: isTopicSelected
                      ? 'rgba(0,0,0,0.02)'
                      : undefined,
                    borderRadius: '4px',
                  }}
                >
                  {RENDER_LIST_ITEM({
                    title: `${
                      defaultOrderEnabled
                        ? topicIndex + 1
                        : topicData.sortingOrder || 0
                    }. ${topicData.name}`,
                    isSelected: isTopicSelected,
                    isSubTopicsOpen,
                    onClick: () => handleTopicChange(topicData),
                    id: `topic-li-${topicId}`,
                    key: `topic-item-${topicId}`,
                    showProgress: userRole === 'Student',
                    progress: getTopicProgress(topicData),
                  })}
                  <Collapse in={isTopicSelected && isSubTopicsOpen}>
                    {subTopics.length ? (
                      <div>
                        {subTopics.map((subTopicData, subTopicIndex) => {
                          const subTopicId = subTopicData._id;
                          const isSubTopicSelected =
                            selectedSubTopic?._id === subTopicId;

                          const subTopicHref = getSubTopicChangeUrl(
                            subTopicData
                          );

                          return RENDER_LIST_ITEM({
                            hideCaret: true,
                            title: `${
                              defaultOrderEnabled
                                ? `${topicIndex + 1}.${subTopicIndex + 1}`
                                : `${topicData.sortingOrder || 0}.${
                                    subTopicData.sortingOrder || 0
                                  }`
                            } ${subTopicData.name}`,
                            isSelected: isSubTopicSelected,
                            id: `sub-topic-li-${topicId}-${subTopicId}`,
                            key: `subtopic-item-${topicIndex}-${subTopicIndex}`,
                            href: subTopicHref,
                            onClick: () => setSelectedSubTopic(subTopicData),
                            cardContentProps: {
                              style: {
                                padding: `16px 5px 16px ${
                                  userRole === 'Student' ? '8px' : '35px'
                                }`,
                              },
                            },
                            showProgress: userRole === 'Student',
                            isCompleted: getSubTopicProgress(
                              topicId,
                              subTopicId
                            ),
                          });
                        })}
                      </div>
                    ) : null}
                  </Collapse>
                </div>
              );
            })
          : null}
      </div>
    </Box>
  );
};

export default NavBarContents;
